import moment from 'moment'

import { MasterCampaignPlanCampaignBuilder, MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'

import { MonthData } from './components/MonthHeader'

export const typeColors: Partial<Record<MasterCampaignPlanCampaignBuilderType, string>> = {
  TV: '#A0634D',
  Audio: '#6F4F7A',
  Internet: '#7A8450',
  Billboard: '#C8A25C'
}

export const offAdCritterPlatformColors: Record<string, string> = {
  LinkedIn: '#1F4F5F',
  Facebook: '#5A7B95',
  Google: '#8B5570',
  Instagram: '#B5764D',
  TikTok: '#5F8585'
}

export const offAdCritterCustomColorOptions = ['#6B8E3D', '#D4A140', '#8B5A3C', '#4D7A7A', '#9B8B6F', '#556B44', '#B87350', '#3E5F52', '#D98B4A', '#6F8F5F']

export const getCampaignColor = (campaign: MasterCampaignPlanCampaignBuilder): string => {
  if (campaign.isOffAdCritter && campaign.offAdCritterName) {
    if (offAdCritterPlatformColors[campaign.offAdCritterName]) {
      return offAdCritterPlatformColors[campaign.offAdCritterName]
    }
    return campaign.offAdCritterColor || typeColors.OffAdCritter || '#999999'
  }
  return typeColors[campaign.campaignType] || '#999999'
}

export const getMomentDuration = (campaign: MasterCampaignPlanCampaignBuilder): moment.unitOfTime.DurationConstructor => {
  return (campaign.intervalUnit?.toLowerCase() || 'months') as moment.unitOfTime.DurationConstructor
}

export const generateMonthsData = (planStartDate: moment.Moment, planDurationMonths: number, minDate: moment.Moment, totalDays: number): MonthData[] => {
  const months: MonthData[] = []
  const totalMonthsToShow = planDurationMonths + 1

  for (let i = 0; i < totalMonthsToShow; i++) {
    const monthDate = minDate.clone().add(i, 'months')
    const position = (i / totalMonthsToShow) * 100
    months.push({
      label: monthDate.format('MMM YYYY'),
      position: position
    })
  }

  return months
}

export const calculateCampaignPosition = (campaign: MasterCampaignPlanCampaignBuilder, minDate: moment.Moment, totalMonthsToShow: number) => {
  const startDate = moment(campaign.startDate)
  const endDate = moment(campaign.startDate).add(campaign.intervalDuration ?? 1, getMomentDuration(campaign))

  const monthWidth = 100 / totalMonthsToShow

  const startMonthIndex = startDate.diff(minDate, 'months')
  const startOfStartMonth = minDate.clone().add(startMonthIndex, 'months')
  const daysInStartMonth = startOfStartMonth.daysInMonth()
  const dayInStartMonth = startDate.date()
  const positionInStartMonth = (dayInStartMonth - 1) / daysInStartMonth
  const leftPosition = startMonthIndex * monthWidth + positionInStartMonth * monthWidth

  const endMonthIndex = endDate.diff(minDate, 'months')
  const startOfEndMonth = minDate.clone().add(endMonthIndex, 'months')
  const daysInEndMonth = startOfEndMonth.daysInMonth()
  const dayInEndMonth = endDate.date()
  const positionInEndMonth = (dayInEndMonth - 1) / daysInEndMonth
  const rightPosition = endMonthIndex * monthWidth + positionInEndMonth * monthWidth

  const width = Math.max(5, rightPosition - leftPosition)
  const actualLeftPosition = Math.max(0, Math.min(leftPosition, 95))
  const actualWidth = Math.min(width, 100 - actualLeftPosition)

  return {
    startDate,
    endDate,
    leftPosition: actualLeftPosition,
    width: actualWidth
  }
}

export const formatDuration = (intervalDuration: number, intervalUnit: moment.unitOfTime.DurationConstructor): string => {
  let unitText: string

  if (intervalUnit.startsWith('week')) {
    unitText = intervalDuration === 1 ? 'week' : 'weeks'
  } else if (intervalUnit.startsWith('month')) {
    unitText = intervalDuration === 1 ? 'month' : 'months'
  } else if (intervalUnit.startsWith('day')) {
    unitText = intervalDuration === 1 ? 'day' : 'days'
  } else if (intervalUnit.startsWith('year')) {
    unitText = intervalDuration === 1 ? 'year' : 'years'
  } else {
    unitText = intervalUnit
  }

  return `(\u00A0${intervalDuration}\u00A0${unitText}\u00A0)`
}

export const sortCampaignsByStartDate = (campaigns: MasterCampaignPlanCampaignBuilder[]): MasterCampaignPlanCampaignBuilder[] => {
  return [...campaigns].sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf())
}

export const organizeCampaignsIntoRows = (campaigns: MasterCampaignPlanCampaignBuilder[], presorted = false): MasterCampaignPlanCampaignBuilder[][] => {
  const sortedCampaigns = presorted ? campaigns : sortCampaignsByStartDate(campaigns)
  const rows: MasterCampaignPlanCampaignBuilder[][] = []

  for (const campaign of sortedCampaigns) {
    const startDate = moment(campaign.startDate)
    const endDate = moment(campaign.startDate).add(campaign.intervalDuration ?? 1, getMomentDuration(campaign))

    let placed = false
    for (const row of rows) {
      const hasOverlap = row.some(existing => {
        const existingStart = moment(existing.startDate)
        const existingEnd = moment(existing.startDate).add(existing.intervalDuration ?? 1, getMomentDuration(existing))
        return startDate.isBefore(existingEnd) && endDate.isAfter(existingStart)
      })

      if (!hasOverlap) {
        row.push(campaign)
        placed = true
        break
      }
    }

    if (!placed) {
      rows.push([campaign])
    }
  }

  return rows
}

export const getAvailableColors = (allColors: string[], usedColors: string[]): string[] => {
  return allColors.filter(color => !usedColors.includes(color))
}

export const getNextAvailableColor = (availableColors: string[]): string => {
  return availableColors[0] || '#999999'
}
